/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {Injectable} from '@angular/core';
import {AbstractSearchCriteriaService} from '../../../shared/filter/abstract-search-criteria.service';
import {SortDirection} from '../../../api/generic/contracts/sort-direction.enum';
import {UserQueryContract} from '../../../api/ms-security/contracts/user-query.contract';
import {GroupTypeEnum} from '../../../api/ms-security/contracts/group-type.enum';
import {UserTableFilterColumnEnum} from '../users-table/models/user-table-filter-column.enum';
import {UserEntityGroupCriterionContract} from '../../../api/ms-security/contracts/user-entity-group-criterion-contract';
import {UserContextEnum} from '../../../api/ms-security/contracts/user-context.enum';

@Injectable({
    providedIn: 'root'
})
export class UserSearchCriteriaService extends AbstractSearchCriteriaService<UserTableFilterColumnEnum> {

    /**
     * Clear all the criteria
     */
    clear(): void {
        // clear stored filters
        this.stateMap.clear();
        // Sort criteria
        this.sortDirection = SortDirection.ASC;
        this.sortedBy = UserTableFilterColumnEnum.IDENTIFICATION;
        // Pagination
        this.pageSize = 20;
        this.pageNumber = 1;
        this.totalResults = 0;
    }

    getUserQuery(context: UserContextEnum): UserQueryContract {
        const identification = this.mapStringCriterion(UserTableFilterColumnEnum.IDENTIFICATION);
        const roleCodes = this.mapCodeCriterion(UserTableFilterColumnEnum.ROLE);
        const partnerCodes = this.mapCodeCriterion(UserTableFilterColumnEnum.PARTNER);
        const entityGroupTechnicianGroupCodes = this.mapCodeCriterion(UserTableFilterColumnEnum.TECHNICIAN);
        const entityGroupSupplyingPackingPlantCodes = this.mapCodeCriterion(UserTableFilterColumnEnum.SUPPLYING_PACKING_PLANT);
        const entityGroupSupplierCodes = this.mapCodeCriterion(UserTableFilterColumnEnum.SUPPLIER);
        const entityGroupCustomerCodes = this.mapCodeCriterion(UserTableFilterColumnEnum.CUSTOMER);
        const entityGroups = this.mapEntityGroupsCriteria(
                partnerCodes,
                entityGroupTechnicianGroupCodes,
                entityGroupSupplyingPackingPlantCodes,
                entityGroupSupplierCodes,
                entityGroupCustomerCodes);

        return {
            searchCriteria: {
                identification,
                roleCodes,
                entityGroups,
                context
            },
            paginationCriteria: this.paginationCriteria
        };
    }

    private mapEntityGroupsCriteria(partnerCodes: string[],
                                    technicianGroupCodes: string[],
                                    supplyingPackingPlantCodes: string[],
                                    suppliers: string[],
                                    customers: string[]): UserEntityGroupCriterionContract[] {
        let entityGroups: UserEntityGroupCriterionContract[];

        if (partnerCodes != null) {
            if (entityGroups == null) {
                entityGroups = [];
            }
            entityGroups.push({
                groupType: GroupTypeEnum.PARTNER,
                codes: partnerCodes
            });
        }

        if (technicianGroupCodes != null) {
            if (entityGroups == null) {
                entityGroups = [];
            }
            entityGroups.push({
                groupType: GroupTypeEnum.TECHNICIAN,
                codes: technicianGroupCodes
            });
        }

        if (supplyingPackingPlantCodes != null) {
            if (entityGroups == null) {
                entityGroups = [];
            }
            entityGroups.push({
                groupType: GroupTypeEnum.PLANNER,
                codes: supplyingPackingPlantCodes
            });
        }

        if (suppliers != null) {
            if (entityGroups == null) {
                entityGroups = [];
            }
            entityGroups.push({
                groupType: GroupTypeEnum.SUPPLIER,
                codes: suppliers
            });
        }

        if (customers != null) {
            if (entityGroups == null) {
                entityGroups = [];
            }
            entityGroups.push({
                groupType: GroupTypeEnum.CUSTOMER,
                codes: customers
            });
        }

        return entityGroups;
    }
}
